// Operator login.
//
// Default: email + password. Phone OTP stays as a collapsible fallback for
// operators who already authed by phone (until SMS provider goes live we
// expect the email path to dominate).

import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth } from '../auth.js';
import { current_language, set_language } from '../i18n/index.js';
import { friendly_error } from '../lib/friendly-error.js';

type Mode = 'password' | 'phone';
type PhoneStage = 'phone' | 'otp';

export function LoginPage(): ReactElement {
  const { send_otp, verify_otp, login_password, request_password_reset } = useAuth();
  const navigate = useNavigate();
  const lang = current_language();

  const [mode, set_mode] = useState<Mode>('password');

  // Password mode
  const [email, set_email] = useState('');
  const [password, set_password] = useState('');

  // Phone OTP mode
  const [phone_stage, set_phone_stage] = useState<PhoneStage>('phone');
  const [phone, set_phone] = useState('+91');
  const [otp, set_otp] = useState('');

  // Shared
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [info, set_info] = useState<string | null>(null);

  const handle_password = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    set_error(null);
    set_info(null);
    set_busy(true);
    try {
      await login_password(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      set_error(friendly_error(err));
    } finally {
      set_busy(false);
    }
  };

  const handle_forgot = async (): Promise<void> => {
    set_error(null);
    if (!email) {
      set_error('Enter your email above first, then click Forgot password.');
      return;
    }
    set_busy(true);
    try {
      await request_password_reset(email);
      set_info(`If ${email} is registered, a new link is on its way.`);
    } catch (err) {
      set_error(friendly_error(err));
    } finally {
      set_busy(false);
    }
  };

  const handle_send_otp = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    set_error(null);
    set_busy(true);
    try {
      await send_otp(phone);
      set_phone_stage('otp');
    } catch (err) {
      set_error(friendly_error(err));
    } finally {
      set_busy(false);
    }
  };

  const handle_verify_otp = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    set_error(null);
    set_busy(true);
    try {
      await verify_otp(phone, otp);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      set_error(friendly_error(err));
    } finally {
      set_busy(false);
    }
  };

  return (
    <main className="min-h-screen bg-bg text-ink flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-surface border border-line rounded-lg p-6 shadow-sm">
        <div className="text-center mb-6">
          <h1 className="font-semibold tracking-tight text-3xl text-primary mb-1">ChitDesk</h1>
          <p className="font-sans text-sm text-ink/60">
            {typeof window !== 'undefined' ? `Sign in to ${window.location.hostname.split('.')[0]}` : 'Sign in'}
          </p>
        </div>

        {mode === 'password' ? (
          <form onSubmit={handle_password} className="flex flex-col gap-3">
            <label className="text-sm">
              <span className="block mb-1">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => set_email(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => set_password(e.target.value)}
                required
                minLength={8}
                autoComplete="current-password"
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
            <button
              type="button"
              onClick={handle_forgot}
              disabled={busy}
              className="text-xs text-ink/60 hover:text-primary self-start"
            >
              Forgot password?
            </button>
            <button
              type="submit"
              disabled={busy}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white py-2 rounded font-medium text-sm transition"
            >
              {busy ? 'Signing in…' : 'Sign in →'}
            </button>
          </form>
        ) : phone_stage === 'phone' ? (
          <form onSubmit={handle_send_otp} className="flex flex-col gap-3">
            <label className="text-sm">
              <span className="block mb-1">Phone</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => set_phone(e.target.value)}
                placeholder="+919876543210"
                pattern="\+91[6-9]\d{9}"
                required
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white py-2 rounded font-medium text-sm transition"
            >
              Send OTP
            </button>
          </form>
        ) : (
          <form onSubmit={handle_verify_otp} className="flex flex-col gap-3">
            <label className="text-sm">
              <span className="block mb-1">OTP</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                value={otp}
                onChange={(e) => set_otp(e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2 border border-line rounded bg-bg-elev font-mono text-lg text-center tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white py-2 rounded font-medium text-sm transition"
            >
              Verify
            </button>
            <button
              type="button"
              onClick={() => set_phone_stage('phone')}
              className="text-xs text-ink/60 hover:text-primary"
            >
              ← {phone}
            </button>
          </form>
        )}

        {error && <div className="mt-4 px-3 py-2 bg-bad-soft text-bad text-xs rounded">{error}</div>}
        {info && <div className="mt-4 px-3 py-2 bg-good-soft text-good text-xs rounded">{info}</div>}

        <div className="mt-6 border-t border-line pt-4 text-center">
          {mode === 'password' ? (
            <button
              type="button"
              onClick={() => { set_mode('phone'); set_error(null); set_info(null); }}
              className="text-xs text-ink/60 hover:text-primary"
            >
              Sign in with phone OTP instead
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { set_mode('password'); set_phone_stage('phone'); set_error(null); set_info(null); }}
              className="text-xs text-ink/60 hover:text-primary"
            >
              Sign in with email + password instead
            </button>
          )}
        </div>

        <div className="mt-2 text-center">
          <Link to="/" className="text-xs text-ink/50 hover:text-primary">← Back</Link>
        </div>

        <button
          type="button"
          onClick={() => set_language(lang === 'ta' ? 'en' : 'ta')}
          className="mt-4 w-full text-center text-xs text-ink/50 hover:text-primary"
        >
          {lang === 'ta' ? 'English' : 'தமிழ்'}
        </button>
      </div>
    </main>
  );
}
